<template>
  <main class="procesos-automaticos">
    <!-- Cabecera -->
    <div class="header-section">
      <div class="row align-items-center mb-3">
        <div class="col-md-8">
          <h4 class="page-title m-0">
            <i class="fa fa-cogs mr-2"></i>
            Procesos Automáticos
          </h4>
          <small class="text-muted">
            Configuración de procesos automáticos para
            <strong v-if="nombreEmpresa">{{ nombreEmpresa }}</strong>
            <span v-else>tu gimnasio</span>.
          </small>
        </div>
        <div class="col-md-4 text-md-right mt-2 mt-md-0">
          <button
            class="btn btn-outline-secondary btn-sm"
            @click="cargarConfiguracion()"
            :disabled="cargandoAlgo"
          >
            <span
              v-if="cargandoAlgo"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            <i v-else class="fa fa-refresh mr-1"></i>
            Refrescar todo
          </button>
        </div>
      </div>
    </div>

    <!-- Loader inicial global (sólo mientras NINGÚN job ha cargado) -->
    <div
      v-if="cargaInicialEnCurso"
      class="text-center py-5"
    >
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-2 text-muted small">Cargando configuración de procesos...</p>
    </div>

    <template v-else>
      <!-- =============================================================== -->
      <!-- SECCIÓN: Notificación de Vencimientos                            -->
      <!-- =============================================================== -->
      <div class="seccion-header">
        <h5 class="m-0">
          <i class="fa fa-envelope-o mr-2 text-primary"></i>
          Notificación de Vencimientos
        </h5>
        <small class="text-muted d-block">
          <code>JOB_NotificarVencimientos</code> — envía un correo a tus socios cuyas
          membresías están por vencer.
        </small>
      </div>

      <!-- Card de error si no cargó -->
      <div v-if="!notificar.configCargada" class="card config-card">
        <div class="card-body text-center py-4">
          <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
          No se pudo cargar la configuración de este proceso.
          <button
            class="btn btn-link btn-sm"
            @click="cargarConfigNotificar()"
            :disabled="notificar.loading.config"
          >
            <span
              v-if="notificar.loading.config"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Reintentar
          </button>
        </div>
      </div>

      <div v-else class="card config-card">
        <div class="card-header config-card-header">
          <div class="header-icon header-icon--purple">
            <i class="fa fa-bell-o"></i>
          </div>
          <div class="flex-grow-1">
            <h5 class="m-0">Notificación de Vencimientos</h5>
            <small class="text-muted">
              Notifica a tus socios sobre el próximo vencimiento de sus membresías
              dentro de la ventana de días configurada.
            </small>
          </div>
        </div>
        <div class="card-body">
          <!-- Switch principal -->
          <div class="d-flex align-items-center justify-content-between flex-wrap">
            <div class="flex-grow-1 mr-3 mb-2 mb-md-0">
              <p class="mb-0 small text-muted">
                Cuando esté <strong>activado</strong>, el job procesará tus socios y
                enviará correos a quienes estén dentro de la ventana de días configurada
                antes del vencimiento de su membresía.
              </p>
            </div>
            <div class="d-flex align-items-center toggle-wrapper">
              <span
                class="estado-label mr-2"
                :class="
                  notificar.guardado.participaEnProceso
                    ? 'text-success'
                    : 'text-muted'
                "
              >
                {{ notificar.guardado.participaEnProceso ? "Activado" : "Desactivado" }}
              </span>
              <el-switch
                :value="notificar.guardado.participaEnProceso"
                :disabled="notificar.loading.toggle"
                active-color="#6bd098"
                inactive-color="#cfcfcf"
                @change="onToggleNotificacion"
              />
              <i
                v-if="notificar.loading.toggle"
                class="fa fa-spinner fa-spin ml-2 text-muted"
              ></i>
            </div>
          </div>

          <!-- Sub-sección: días (sólo cuando participa) -->
          <div
            v-if="notificar.guardado.participaEnProceso"
            class="dias-section mt-4"
          >
            <h6 class="font-weight-bold mb-1">
              <i class="fa fa-hourglass-half mr-1"></i>
              Días antes del vencimiento
            </h6>
            <small class="text-muted d-block mb-3">
              Cantidad de días antes del vencimiento en que se enviará el correo de
              aviso a tus socios. Rango permitido:
              <strong>
                {{ notificar.global.diasMinimoPermitido }} a
                {{ notificar.global.diasMaximoPermitido }}
              </strong>.
              Por defecto:
              <strong>{{ notificar.global.diasDefault }} días</strong>.
            </small>

            <div class="row align-items-start">
              <div class="col-md-4 col-sm-6">
                <label class="form-label small font-weight-bold mb-1">
                  Días configurados
                </label>
                <input
                  v-model="notificar.diasInput"
                  type="number"
                  :min="notificar.global.diasMinimoPermitido"
                  :max="notificar.global.diasMaximoPermitido"
                  class="form-control"
                  :placeholder="placeholderDiasNotificar"
                  :disabled="
                    notificar.loading.guardarDias || notificar.loading.eliminarDias
                  "
                />
                <small
                  v-if="notificar.diasInput && !diasValidoNotificar"
                  class="text-danger d-block mt-1"
                >
                  <i class="fa fa-exclamation-triangle mr-1"></i>
                  Debe ser un entero entre
                  {{ notificar.global.diasMinimoPermitido }} y
                  {{ notificar.global.diasMaximoPermitido }}.
                </small>
                <small class="text-muted d-block mt-1">
                  <i class="fa fa-info-circle mr-1"></i>
                  Valor efectivamente aplicado por el job:
                  <strong>{{ diasEfectivosTextoNotificar }}</strong>
                </small>
              </div>
              <div class="col-md-8 col-sm-6 mt-3 mt-sm-0 acciones-dias">
                <button
                  class="btn btn-primary btn-sm"
                  @click="guardarDiasNotificar()"
                  :disabled="
                    !cambioDiasNotificar ||
                    !diasValidoNotificar ||
                    notificar.loading.guardarDias ||
                    notificar.loading.eliminarDias
                  "
                >
                  <span
                    v-if="notificar.loading.guardarDias"
                    class="spinner-border spinner-border-sm mr-1"
                  ></span>
                  <i v-else class="fa fa-save mr-1"></i>
                  Guardar días
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="resetDiasInputNotificar()"
                  :disabled="
                    !cambioDiasNotificar ||
                    notificar.loading.guardarDias ||
                    notificar.loading.eliminarDias
                  "
                >
                  <i class="fa fa-undo mr-1"></i>
                  Descartar
                </button>
                <el-tooltip placement="top" :open-delay="300">
                  <template #content>
                    Elimina el valor configurado y vuelve al valor por defecto
                    ({{ notificar.global.diasDefault }} días)
                  </template>
                  <button
                    class="btn btn-outline-danger btn-sm"
                    @click="eliminarDiasNotificarConfirmado()"
                    :disabled="
                      !notificar.guardado.tieneDiasPersonalizados ||
                      notificar.loading.eliminarDias ||
                      notificar.loading.guardarDias
                    "
                  >
                    <span
                      v-if="notificar.loading.eliminarDias"
                      class="spinner-border spinner-border-sm mr-1"
                    ></span>
                    <i v-else class="fa fa-eraser mr-1"></i>
                    Volver al valor por defecto
                  </button>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="latencia-note">
        <i class="fa fa-clock-o mr-1"></i>
        El proceso se ejecuta <strong>una vez al día</strong> (08:00 hora servidor
        en producción). Los cambios se aplican en la
        <strong>próxima ejecución matutina</strong>.
      </p>

      <!-- =============================================================== -->
      <!-- SECCIÓN: Normalización de Membresías                             -->
      <!-- =============================================================== -->
      <div class="seccion-header mt-4">
        <h5 class="m-0">
          <i class="fa fa-refresh mr-2 text-success"></i>
          Normalización de Membresías
        </h5>
        <small class="text-muted d-block">
          <code>JOB_NormalizarMembresias</code> — activa membresías en estado PAGADO
          según las reglas configuradas.
        </small>
      </div>

      <!-- Card de error si no cargó -->
      <div v-if="!normalizar.configCargada" class="card config-card">
        <div class="card-body text-center py-4">
          <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
          No se pudo cargar la configuración de este proceso.
          <button
            class="btn btn-link btn-sm"
            @click="cargarConfigNormalizar()"
            :disabled="normalizar.loading.config"
          >
            <span
              v-if="normalizar.loading.config"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Reintentar
          </button>
        </div>
      </div>

      <template v-else>
        <!-- Card 1: Activación por Fecha de Inicio -->
        <div class="card config-card">
          <div class="card-header config-card-header">
            <div class="header-icon header-icon--blue">
              <i class="fa fa-calendar-check-o"></i>
            </div>
            <div class="flex-grow-1">
              <h5 class="m-0">Activación por Fecha de Inicio</h5>
              <small class="text-muted">
                Activa automáticamente las membresías
                <span class="text-danger font-weight-bold">PAGADO</span>
                cuya
                <code>fechaInicio</code>
                corresponda al día actual.
              </small>
            </div>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between flex-wrap">
              <div class="flex-grow-1 mr-3 mb-2 mb-md-0">
                <p class="mb-0 small text-muted">
                  Cuando esté <strong>activado</strong>, el job procesará en cada ciclo
                  las membresías pagadas que tengan fecha de inicio igual a la fecha
                  actual y las dejará en estado activo.
                </p>
              </div>
              <div class="d-flex align-items-center toggle-wrapper">
                <span
                  class="estado-label mr-2"
                  :class="
                    normalizar.guardado.incluidaActivacionFechaInicio
                      ? 'text-success'
                      : 'text-muted'
                  "
                >
                  {{
                    normalizar.guardado.incluidaActivacionFechaInicio
                      ? "Activado"
                      : "Desactivado"
                  }}
                </span>
                <el-switch
                  :value="normalizar.guardado.incluidaActivacionFechaInicio"
                  :disabled="normalizar.loading.toggleFI"
                  active-color="#6bd098"
                  inactive-color="#cfcfcf"
                  @change="onToggleFechaInicio"
                />
                <i
                  v-if="normalizar.loading.toggleFI"
                  class="fa fa-spinner fa-spin ml-2 text-muted"
                ></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 2: Activación Automática por Días -->
        <div class="card config-card">
          <div class="card-header config-card-header">
            <div class="header-icon header-icon--green">
              <i class="fa fa-clock-o"></i>
            </div>
            <div class="flex-grow-1">
              <h5 class="m-0">Activación Automática por Días</h5>
              <small class="text-muted">
                Activa membresías
                <span class="text-danger font-weight-bold">PAGADO</span>
                <em>sin</em>
                <code>fechaInicio</code> definida tras
                <strong>N días</strong> desde su asignación.
              </small>
            </div>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between flex-wrap">
              <div class="flex-grow-1 mr-3 mb-2 mb-md-0">
                <p class="mb-0 small text-muted">
                  Cuando esté <strong>activado</strong>, el job procesará en cada ciclo
                  las membresías pagadas sin fecha de inicio definida una vez
                  transcurridos los días configurados desde su asignación.
                </p>
              </div>
              <div class="d-flex align-items-center toggle-wrapper">
                <span
                  class="estado-label mr-2"
                  :class="
                    normalizar.guardado.incluidaActivacionAutomatica
                      ? 'text-success'
                      : 'text-muted'
                  "
                >
                  {{
                    normalizar.guardado.incluidaActivacionAutomatica
                      ? "Activado"
                      : "Desactivado"
                  }}
                </span>
                <el-switch
                  :value="normalizar.guardado.incluidaActivacionAutomatica"
                  :disabled="normalizar.loading.toggleAA"
                  active-color="#6bd098"
                  inactive-color="#cfcfcf"
                  @change="onToggleAutomatica"
                />
                <i
                  v-if="normalizar.loading.toggleAA"
                  class="fa fa-spinner fa-spin ml-2 text-muted"
                ></i>
              </div>
            </div>

            <div
              v-if="normalizar.guardado.incluidaActivacionAutomatica"
              class="dias-section mt-4"
            >
              <h6 class="font-weight-bold mb-1">
                <i class="fa fa-hourglass-half mr-1"></i> Días de espera
              </h6>
              <small class="text-muted d-block mb-3">
                Cantidad de días que el job esperará desde la asignación de la membresía
                antes de activarla. Rango válido: <strong>1 a 365</strong>.
                Si no configuras un valor, el job usará el fallback por defecto de
                <strong>{{ FALLBACK_DIAS_NM }} días</strong>.
              </small>

              <div class="row align-items-start">
                <div class="col-md-4 col-sm-6">
                  <label class="form-label small font-weight-bold mb-1">
                    Días configurados
                  </label>
                  <input
                    v-model="normalizar.diasInput"
                    type="number"
                    min="1"
                    max="365"
                    class="form-control"
                    :placeholder="placeholderDiasNormalizar"
                    :disabled="
                      normalizar.loading.guardarDias ||
                      normalizar.loading.eliminarDias
                    "
                  />
                  <small
                    v-if="normalizar.diasInput && !diasValidoNormalizar"
                    class="text-danger d-block mt-1"
                  >
                    <i class="fa fa-exclamation-triangle mr-1"></i>
                    Debe ser un entero entre 1 y 365.
                  </small>
                  <small class="text-muted d-block mt-1">
                    <i class="fa fa-info-circle mr-1"></i>
                    Valor efectivamente aplicado por el job:
                    <strong>{{ diasEfectivosTextoNormalizar }}</strong>
                  </small>
                </div>
                <div class="col-md-8 col-sm-6 mt-3 mt-sm-0 acciones-dias">
                  <button
                    class="btn btn-primary btn-sm"
                    @click="guardarDiasNormalizar()"
                    :disabled="
                      !cambioDiasNormalizar ||
                      !diasValidoNormalizar ||
                      normalizar.loading.guardarDias ||
                      normalizar.loading.eliminarDias
                    "
                  >
                    <span
                      v-if="normalizar.loading.guardarDias"
                      class="spinner-border spinner-border-sm mr-1"
                    ></span>
                    <i v-else class="fa fa-save mr-1"></i>
                    Guardar días
                  </button>
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="resetDiasInputNormalizar()"
                    :disabled="
                      !cambioDiasNormalizar ||
                      normalizar.loading.guardarDias ||
                      normalizar.loading.eliminarDias
                    "
                  >
                    <i class="fa fa-undo mr-1"></i>
                    Descartar
                  </button>
                  <el-tooltip placement="top" :open-delay="300">
                    <template #content>
                      Elimina el valor configurado y vuelve al fallback por defecto
                    </template>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      @click="eliminarDiasNormalizarConfirmado()"
                      :disabled="
                        !normalizar.guardado.tieneDiasActivacionConfigurados ||
                        normalizar.loading.eliminarDias ||
                        normalizar.loading.guardarDias
                      "
                    >
                      <span
                        v-if="normalizar.loading.eliminarDias"
                        class="spinner-border spinner-border-sm mr-1"
                      ></span>
                      <i v-else class="fa fa-eraser mr-1"></i>
                      Usar valor por defecto ({{ FALLBACK_DIAS_NM }} días)
                    </button>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p class="latencia-note">
          <i class="fa fa-clock-o mr-1"></i>
          El proceso se ejecuta en
          <strong>ciclos cortos</strong>
          (≤ 15 minutos en producción, ≤ 1 minuto en desarrollo). Los cambios se
          aplican en el siguiente ciclo.
        </p>
      </template>

      <!-- =============================================================== -->
      <!-- SECCIÓN: Activación de Membresías Secundarias                    -->
      <!-- =============================================================== -->
      <div class="seccion-header mt-4">
        <h5 class="m-0">
          <i class="fa fa-magic mr-2 text-warning"></i>
          Activación de Membresías Secundarias
        </h5>
        <small class="text-muted d-block">
          <code>JOB_ActivarMembresiasSecundarias</code> — promueve automáticamente
          la membresía secundaria PAGADO a principal cuando la principal del
          socio está vencida.
        </small>
      </div>

      <!-- Card de error si no cargó -->
      <div v-if="!activarSecundarias.configCargada" class="card config-card">
        <div class="card-body text-center py-4">
          <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
          No se pudo cargar la configuración de este proceso.
          <button
            class="btn btn-link btn-sm"
            @click="cargarConfigActivarSecundarias()"
            :disabled="activarSecundarias.loading.config"
          >
            <span
              v-if="activarSecundarias.loading.config"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            Reintentar
          </button>
        </div>
      </div>

      <div v-else class="card config-card">
        <div class="card-header config-card-header">
          <div class="header-icon header-icon--orange">
            <i class="fa fa-magic"></i>
          </div>
          <div class="flex-grow-1">
            <h5 class="m-0">Activación de Membresías Secundarias</h5>
            <small class="text-muted">
              Cuando la membresía principal del socio esté
              <span class="text-danger font-weight-bold">PENDIENTE</span>,
              promueve automáticamente la siguiente membresía secundaria en
              estado
              <span class="text-danger font-weight-bold">PAGADO</span>
              al rol de principal (la activa como
              <strong>VIGENTE</strong> y la guarda en historial).
            </small>
          </div>
        </div>
        <div class="card-body">
          <div class="d-flex align-items-center justify-content-between flex-wrap">
            <div class="flex-grow-1 mr-3 mb-2 mb-md-0">
              <p class="mb-0 small text-muted">
                Cuando esté <strong>activado</strong>, el job evaluará en cada
                ciclo las membresías secundarias PAGADO de tu empresa y promoverá
                la más apropiada cuando la principal del socio haya vencido.
                Si está <strong>desactivado</strong>, las secundarias quedan
                congeladas y deberás activarlas manualmente.
              </p>
            </div>
            <div class="d-flex align-items-center toggle-wrapper">
              <span
                class="estado-label mr-2"
                :class="
                  activarSecundarias.guardado.participaEnProceso
                    ? 'text-success'
                    : 'text-muted'
                "
              >
                {{
                  activarSecundarias.guardado.participaEnProceso
                    ? "Activado"
                    : "Desactivado"
                }}
              </span>
              <el-switch
                :value="activarSecundarias.guardado.participaEnProceso"
                :disabled="activarSecundarias.loading.toggle"
                active-color="#6bd098"
                inactive-color="#cfcfcf"
                @change="onToggleActivarSecundarias"
              />
              <i
                v-if="activarSecundarias.loading.toggle"
                class="fa fa-spinner fa-spin ml-2 text-muted"
              ></i>
            </div>
          </div>
        </div>
      </div>

      <p class="latencia-note">
        <i class="fa fa-clock-o mr-1"></i>
        El proceso se ejecuta <strong>una vez al día</strong> (05:00 hora servidor
        en producción, cada 1 minuto en desarrollo). Los cambios se aplican en la
        <strong>próxima ejecución</strong>.
      </p>

      <!-- Pie de página: auditoría -->
      <div v-if="modificadoPor" class="text-right text-muted small mt-3">
        <i class="fa fa-user mr-1"></i>
        Última modificación por
        <strong>{{ modificadoPor.nombreUsuario || "(usuario desconocido)" }}</strong>
      </div>
    </template>
  </main>
</template>

<script>
import { Switch, Tooltip } from "element-ui";

const TOKEN_KEY = "jCxbraoQeD";

const JOB_NM = "JOB_NormalizarMembresias";
const JOB_NV = "JOB_NotificarVencimientos";
const JOB_AS = "JOB_ActivarMembresiasSecundarias";
const FALLBACK_DIAS_NM = 15;

const OP = {
  // JOB_NormalizarMembresias
  NM_CONSULTAR: "/v1/job-config/mi-empresa/consultar",
  NM_TOGGLE_FI: "/v1/job-config/mi-empresa/actualizar-inclusion-fecha-inicio",
  NM_TOGGLE_AA: "/v1/job-config/mi-empresa/actualizar-inclusion-automatica",
  NM_DIAS: "/v1/job-config/mi-empresa/actualizar-dias-activacion-automatica",
  NM_DIAS_DEL: "/v1/job-config/mi-empresa/eliminar-dias-activacion-automatica",
  // JOB_NotificarVencimientos
  NV_CONSULTAR: "/v1/job-config/notificar-vencimientos/mi-empresa/consultar",
  NV_TOGGLE: "/v1/job-config/notificar-vencimientos/mi-empresa/actualizar-participacion",
  NV_DIAS: "/v1/job-config/notificar-vencimientos/mi-empresa/actualizar-dias",
  NV_DIAS_DEL: "/v1/job-config/notificar-vencimientos/mi-empresa/eliminar-dias",
  // JOB_ActivarMembresiasSecundarias
  AS_CONSULTAR: "/v1/job-config/activar-planes-secundarios/mi-empresa/consultar",
  AS_TOGGLE: "/v1/job-config/activar-planes-secundarios/mi-empresa/actualizar-participacion",
};

export default {
  name: "ProcesosAutomaticos",
  components: {
    [Switch.name]: Switch,
    [Tooltip.name]: Tooltip,
  },
  data() {
    return {
      FALLBACK_DIAS_NM,

      // ---- Compartido ----
      nombreEmpresa: null,
      modificadoPor: null,

      // ---- JOB_NormalizarMembresias ----
      normalizar: {
        configCargada: false,
        guardado: {
          incluidaActivacionFechaInicio: false,
          incluidaActivacionAutomatica: false,
          tieneDiasActivacionConfigurados: false,
          diasActivacionAutomatica: null,
          diasActivacionEfectivos: null,
        },
        diasInput: "",
        loading: {
          config: false,
          toggleFI: false,
          toggleAA: false,
          guardarDias: false,
          eliminarDias: false,
        },
      },

      // ---- JOB_NotificarVencimientos ----
      notificar: {
        configCargada: false,
        guardado: {
          participaEnProceso: false,
          tieneDiasPersonalizados: false,
          diasNotificacionPersonalizados: null,
          diasNotificacionEfectivos: null,
        },
        global: {
          diasDefault: null,
          diasMax: null,
          diasMinimoPermitido: 1,
          diasMaximoPermitido: 30,
        },
        diasInput: "",
        loading: {
          config: false,
          toggle: false,
          guardarDias: false,
          eliminarDias: false,
        },
      },

      // ---- JOB_ActivarMembresiasSecundarias ----
      activarSecundarias: {
        configCargada: false,
        guardado: {
          participaEnProceso: false,
        },
        loading: {
          config: false,
          toggle: false,
        },
      },
    };
  },
  computed: {
    cargandoAlgo() {
      return (
        this.normalizar.loading.config ||
        this.notificar.loading.config ||
        this.activarSecundarias.loading.config ||
        this.normalizar.loading.toggleFI ||
        this.normalizar.loading.toggleAA ||
        this.normalizar.loading.guardarDias ||
        this.normalizar.loading.eliminarDias ||
        this.notificar.loading.toggle ||
        this.notificar.loading.guardarDias ||
        this.notificar.loading.eliminarDias ||
        this.activarSecundarias.loading.toggle
      );
    },
    cargaInicialEnCurso() {
      // Mostramos el spinner global sólo mientras NO se haya cargado nada y haya algo en vuelo.
      const algunaCargada =
        this.normalizar.configCargada ||
        this.notificar.configCargada ||
        this.activarSecundarias.configCargada;
      const algunaEnCurso =
        this.normalizar.loading.config ||
        this.notificar.loading.config ||
        this.activarSecundarias.loading.config;
      return algunaEnCurso && !algunaCargada;
    },

    // ---- Computeds JOB_NormalizarMembresias ----
    placeholderDiasNormalizar() {
      return this.normalizar.guardado.tieneDiasActivacionConfigurados
        ? ""
        : `${FALLBACK_DIAS_NM} (por defecto)`;
    },
    diasValidoNormalizar() {
      const v = this.normalizar.diasInput;
      const n = parseInt(v, 10);
      return (
        !isNaN(n) && n >= 1 && n <= 365 && String(n) === String(v).trim()
      );
    },
    cambioDiasNormalizar() {
      const actual = this.normalizar.diasInput;
      const actualVacio = actual === "" || actual === null || actual === undefined;
      if (!this.normalizar.guardado.tieneDiasActivacionConfigurados) {
        return !actualVacio;
      }
      if (actualVacio) return true;
      const n = parseInt(actual, 10);
      return n !== this.normalizar.guardado.diasActivacionAutomatica;
    },
    diasEfectivosTextoNormalizar() {
      const ef = this.normalizar.guardado.diasActivacionEfectivos;
      if (ef === null || ef === undefined) return "—";
      if (!this.normalizar.guardado.tieneDiasActivacionConfigurados) {
        return `${ef} días (fallback por defecto)`;
      }
      return `${ef} días`;
    },

    // ---- Computeds JOB_NotificarVencimientos ----
    placeholderDiasNotificar() {
      if (this.notificar.guardado.tieneDiasPersonalizados) return "";
      const d = this.notificar.global.diasDefault;
      return d != null ? `${d} (por defecto)` : "Por defecto";
    },
    diasValidoNotificar() {
      const v = this.notificar.diasInput;
      const n = parseInt(v, 10);
      const min = this.notificar.global.diasMinimoPermitido;
      const max = this.notificar.global.diasMaximoPermitido;
      return (
        !isNaN(n) &&
        n >= min &&
        n <= max &&
        String(n) === String(v).trim()
      );
    },
    cambioDiasNotificar() {
      const actual = this.notificar.diasInput;
      const actualVacio = actual === "" || actual === null || actual === undefined;
      if (!this.notificar.guardado.tieneDiasPersonalizados) {
        return !actualVacio;
      }
      if (actualVacio) return true;
      const n = parseInt(actual, 10);
      return n !== this.notificar.guardado.diasNotificacionPersonalizados;
    },
    diasEfectivosTextoNotificar() {
      const ef = this.notificar.guardado.diasNotificacionEfectivos;
      if (ef === null || ef === undefined) return "—";
      if (!this.notificar.guardado.tieneDiasPersonalizados) {
        return `${ef} días (por defecto)`;
      }
      return `${ef} días`;
    },
  },
  async mounted() {
    await this.cargarConfiguracion();
  },
  methods: {
    // =================================================================
    //  Carga inicial / refresh
    // =================================================================
    async cargarConfiguracion() {
      // Carga los jobs en paralelo. Un fallo aislado no bloquea a los demás.
      await Promise.allSettled([
        this.cargarConfigNormalizar(),
        this.cargarConfigNotificar(),
        this.cargarConfigActivarSecundarias(),
      ]);
    },
    async cargarConfigNormalizar() {
      this.normalizar.loading.config = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NM_CONSULTAR, {
          jobName: JOB_NM,
        });
        this.aplicarConfiguracionNormalizar(dataOut);
        this.normalizar.configCargada = true;
      } catch (err) {
        this.normalizar.configCargada = false;
        this.$logger.error(
          "[ProcesosAutomaticos] Error cargando JOB_NormalizarMembresias:",
          err
        );
      } finally {
        this.normalizar.loading.config = false;
      }
    },
    async cargarConfigNotificar() {
      this.notificar.loading.config = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NV_CONSULTAR, {
          jobName: JOB_NV,
        });
        this.aplicarConfiguracionNotificar(dataOut);
        this.notificar.configCargada = true;
      } catch (err) {
        this.notificar.configCargada = false;
        this.$logger.error(
          "[ProcesosAutomaticos] Error cargando JOB_NotificarVencimientos:",
          err
        );
      } finally {
        this.notificar.loading.config = false;
      }
    },
    aplicarConfiguracionNormalizar(dataOut) {
      if (!dataOut) return;
      if (dataOut.nombreEmpresa) this.nombreEmpresa = dataOut.nombreEmpresa;
      const c = dataOut.configuracionEmpresa || {};
      this.normalizar.guardado = {
        incluidaActivacionFechaInicio: !!c.incluidaActivacionFechaInicio,
        incluidaActivacionAutomatica: !!c.incluidaActivacionAutomatica,
        tieneDiasActivacionConfigurados: !!c.tieneDiasActivacionConfigurados,
        diasActivacionAutomatica:
          c.diasActivacionAutomatica !== undefined
            ? c.diasActivacionAutomatica
            : null,
        diasActivacionEfectivos:
          c.diasActivacionEfectivos !== undefined
            ? c.diasActivacionEfectivos
            : null,
      };
      this.normalizar.diasInput = this.normalizar.guardado
        .tieneDiasActivacionConfigurados
        ? String(this.normalizar.guardado.diasActivacionAutomatica)
        : "";
    },
    aplicarConfiguracionNotificar(dataOut) {
      if (!dataOut) return;
      if (dataOut.nombreEmpresa) this.nombreEmpresa = dataOut.nombreEmpresa;
      const c = dataOut.configuracionEmpresa || {};
      const g = dataOut.configuracionGlobal || {};
      this.notificar.guardado = {
        participaEnProceso: !!c.participaEnProceso,
        tieneDiasPersonalizados: !!c.tieneDiasPersonalizados,
        diasNotificacionPersonalizados:
          c.diasNotificacionPersonalizados !== undefined
            ? c.diasNotificacionPersonalizados
            : null,
        diasNotificacionEfectivos:
          c.diasNotificacionEfectivos !== undefined
            ? c.diasNotificacionEfectivos
            : null,
      };
      this.notificar.global = {
        diasDefault: g.diasDefault !== undefined ? g.diasDefault : null,
        diasMax: g.diasMax !== undefined ? g.diasMax : null,
        diasMinimoPermitido:
          g.diasMinimoPermitido !== undefined ? g.diasMinimoPermitido : 1,
        diasMaximoPermitido:
          g.diasMaximoPermitido !== undefined ? g.diasMaximoPermitido : 30,
      };
      this.notificar.diasInput = this.notificar.guardado.tieneDiasPersonalizados
        ? String(this.notificar.guardado.diasNotificacionPersonalizados)
        : "";
    },
    async cargarConfigActivarSecundarias() {
      this.activarSecundarias.loading.config = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.AS_CONSULTAR, {
          jobName: JOB_AS,
        });
        this.aplicarConfiguracionActivarSecundarias(dataOut);
        this.activarSecundarias.configCargada = true;
      } catch (err) {
        this.activarSecundarias.configCargada = false;
        this.$logger.error(
          "[ProcesosAutomaticos] Error cargando JOB_ActivarMembresiasSecundarias:",
          err
        );
      } finally {
        this.activarSecundarias.loading.config = false;
      }
    },
    aplicarConfiguracionActivarSecundarias(dataOut) {
      if (!dataOut) return;
      if (dataOut.nombreEmpresa) this.nombreEmpresa = dataOut.nombreEmpresa;
      const c = dataOut.configuracionEmpresa || {};
      this.activarSecundarias.guardado = {
        participaEnProceso: !!c.participaEnProceso,
      };
    },

    // =================================================================
    //  JOB_NormalizarMembresias — acciones
    // =================================================================
    async onToggleFechaInicio(nuevoValor) {
      if (this.normalizar.loading.toggleFI) return;
      this.normalizar.loading.toggleFI = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NM_TOGGLE_FI, {
          jobName: JOB_NM,
          incluida: !!nuevoValor,
        });
        this.normalizar.guardado.incluidaActivacionFechaInicio = !!nuevoValor;
        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          nuevoValor
            ? "Activación por fecha de inicio: ACTIVADA."
            : "Activación por fecha de inicio: DESACTIVADA."
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.normalizar.loading.toggleFI = false;
      }
    },
    async onToggleAutomatica(nuevoValor) {
      if (this.normalizar.loading.toggleAA) return;
      this.normalizar.loading.toggleAA = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NM_TOGGLE_AA, {
          jobName: JOB_NM,
          incluida: !!nuevoValor,
        });
        this.normalizar.guardado.incluidaActivacionAutomatica = !!nuevoValor;
        if (!nuevoValor) {
          this.normalizar.guardado.diasActivacionEfectivos = null;
        } else if (this.normalizar.guardado.tieneDiasActivacionConfigurados) {
          this.normalizar.guardado.diasActivacionEfectivos =
            this.normalizar.guardado.diasActivacionAutomatica;
        } else {
          this.normalizar.guardado.diasActivacionEfectivos = FALLBACK_DIAS_NM;
        }
        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          nuevoValor
            ? "Activación automática por días: ACTIVADA."
            : "Activación automática por días: DESACTIVADA."
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.normalizar.loading.toggleAA = false;
      }
    },
    async guardarDiasNormalizar() {
      if (!this.cambioDiasNormalizar || !this.diasValidoNormalizar) return;
      this.normalizar.loading.guardarDias = true;
      try {
        const dias = parseInt(this.normalizar.diasInput, 10);
        const dataOut = await this.callMiEmpresa(OP.NM_DIAS, {
          jobName: JOB_NM,
          dias,
        });
        this.normalizar.guardado.diasActivacionAutomatica = dias;
        this.normalizar.guardado.tieneDiasActivacionConfigurados = true;
        if (this.normalizar.guardado.incluidaActivacionAutomatica) {
          this.normalizar.guardado.diasActivacionEfectivos = dias;
        }
        this.aplicarModificadoPor(dataOut);
        this.toastExito(`Días configurados: ${dias} días.`);
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.normalizar.loading.guardarDias = false;
      }
    },
    resetDiasInputNormalizar() {
      this.normalizar.diasInput = this.normalizar.guardado
        .tieneDiasActivacionConfigurados
        ? String(this.normalizar.guardado.diasActivacionAutomatica)
        : "";
    },
    async eliminarDiasNormalizarConfirmado() {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "¿Volver al valor por defecto?",
        html:
          `Se eliminará el valor configurado y la activación automática usará el ` +
          `<strong>fallback de ${FALLBACK_DIAS_NM} días</strong>.`,
        showCancelButton: true,
        confirmButtonText: "Sí, usar fallback",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#ef8157",
        cancelButtonColor: "#6bd098",
        focusCancel: true,
      });
      if (!result.isConfirmed) return;

      this.normalizar.loading.eliminarDias = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NM_DIAS_DEL, {
          jobName: JOB_NM,
        });
        this.normalizar.guardado.tieneDiasActivacionConfigurados = false;
        this.normalizar.guardado.diasActivacionAutomatica = null;
        this.normalizar.guardado.diasActivacionEfectivos = this.normalizar.guardado
          .incluidaActivacionAutomatica
          ? FALLBACK_DIAS_NM
          : null;
        this.normalizar.diasInput = "";
        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          `Días restablecidos al fallback por defecto (${FALLBACK_DIAS_NM} días).`
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.normalizar.loading.eliminarDias = false;
      }
    },

    // =================================================================
    //  JOB_NotificarVencimientos — acciones
    // =================================================================
    async onToggleNotificacion(nuevoValor) {
      if (this.notificar.loading.toggle) return;

      // Confirmación destructiva al desactivar — el impacto es comercial
      // (los socios dejan de recibir notificaciones).
      if (!nuevoValor) {
        const result = await this.$swal.fire({
          icon: "warning",
          title: "¿Desactivar notificaciones de vencimiento?",
          html:
            `Tus socios <strong>DEJARÁN</strong> de recibir correos sobre el ` +
            `próximo vencimiento de sus membresías.<br/><br/>` +
            `<small class="text-muted">` +
            `Si tienes un valor de días personalizado configurado, se eliminará ` +
            `automáticamente. Al reactivar el proceso, la empresa volverá al valor ` +
            `por defecto (${this.notificar.global.diasDefault || "?"} días) hasta ` +
            `que vuelvas a configurar uno propio.` +
            `</small>`,
          showCancelButton: true,
          confirmButtonText: "Sí, desactivar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#ef8157",
          cancelButtonColor: "#6bd098",
          focusCancel: true,
        });
        if (!result.isConfirmed) return;
      }

      this.notificar.loading.toggle = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NV_TOGGLE, {
          jobName: JOB_NV,
          activa: !!nuevoValor,
        });
        this.notificar.guardado.participaEnProceso = !!nuevoValor;

        // El backend nos confirma si eliminó el override de días.
        if (dataOut && dataOut.overrideDiasEliminado) {
          this.notificar.guardado.tieneDiasPersonalizados = false;
          this.notificar.guardado.diasNotificacionPersonalizados = null;
          this.notificar.diasInput = "";
        }

        // Re-derivar días efectivos según las reglas de negocio:
        if (!nuevoValor) {
          this.notificar.guardado.diasNotificacionEfectivos = null;
        } else if (this.notificar.guardado.tieneDiasPersonalizados) {
          this.notificar.guardado.diasNotificacionEfectivos =
            this.notificar.guardado.diasNotificacionPersonalizados;
        } else {
          this.notificar.guardado.diasNotificacionEfectivos =
            this.notificar.global.diasDefault;
        }

        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          nuevoValor
            ? "Notificación de vencimientos: ACTIVADA."
            : "Notificación de vencimientos: DESACTIVADA."
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.notificar.loading.toggle = false;
      }
    },
    async guardarDiasNotificar() {
      if (!this.cambioDiasNotificar || !this.diasValidoNotificar) return;
      this.notificar.loading.guardarDias = true;
      try {
        const dias = parseInt(this.notificar.diasInput, 10);
        const dataOut = await this.callMiEmpresa(OP.NV_DIAS, {
          jobName: JOB_NV,
          dias,
        });
        this.notificar.guardado.diasNotificacionPersonalizados = dias;
        this.notificar.guardado.tieneDiasPersonalizados = true;
        if (this.notificar.guardado.participaEnProceso) {
          this.notificar.guardado.diasNotificacionEfectivos = dias;
        }
        this.aplicarModificadoPor(dataOut);
        this.toastExito(`Días configurados: ${dias} días.`);
      } catch (err) {
        // Caso 409: la empresa fue excluida en otra pestaña/ventana mientras
        // este formulario aún la mostraba como activa. El backend rechaza el
        // guardado de días sobre una empresa excluida.
        if (this.esConflictoExclusion(err)) {
          await this.cargarConfigNotificar();
          this.$swal.fire({
            icon: "warning",
            title: "No se pueden guardar los días",
            html:
              `<strong>Activa primero la notificación de vencimientos</strong> ` +
              `para esta empresa antes de configurar los días.<br/><br/>` +
              `<small class="text-muted">` +
              `El estado de la pantalla se acaba de actualizar — vuelve a intentar ` +
              `tras activar el proceso.` +
              `</small>`,
            confirmButtonColor: "#6bd098",
          });
        } else {
          this.mostrarErrorGuardado(err);
        }
      } finally {
        this.notificar.loading.guardarDias = false;
      }
    },
    resetDiasInputNotificar() {
      this.notificar.diasInput = this.notificar.guardado.tieneDiasPersonalizados
        ? String(this.notificar.guardado.diasNotificacionPersonalizados)
        : "";
    },
    async eliminarDiasNotificarConfirmado() {
      const valorDefault = this.notificar.global.diasDefault;
      const result = await this.$swal.fire({
        icon: "warning",
        title: "¿Volver al valor por defecto?",
        html:
          `Se eliminará el valor personalizado configurado y la notificación ` +
          `usará el valor por defecto (<strong>${valorDefault} días</strong>).`,
        showCancelButton: true,
        confirmButtonText: "Sí, usar valor por defecto",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#ef8157",
        cancelButtonColor: "#6bd098",
        focusCancel: true,
      });
      if (!result.isConfirmed) return;

      this.notificar.loading.eliminarDias = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.NV_DIAS_DEL, {
          jobName: JOB_NV,
        });
        this.notificar.guardado.tieneDiasPersonalizados = false;
        this.notificar.guardado.diasNotificacionPersonalizados = null;
        // El backend devuelve `diasDefaultActual` por si cambió mientras tanto.
        if (dataOut && dataOut.diasDefaultActual !== undefined) {
          this.notificar.global.diasDefault = dataOut.diasDefaultActual;
        }
        if (this.notificar.guardado.participaEnProceso) {
          this.notificar.guardado.diasNotificacionEfectivos =
            this.notificar.global.diasDefault;
        } else {
          this.notificar.guardado.diasNotificacionEfectivos = null;
        }
        this.notificar.diasInput = "";
        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          `Días restablecidos al valor por defecto (${this.notificar.global.diasDefault} días).`
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.notificar.loading.eliminarDias = false;
      }
    },

    // =================================================================
    //  JOB_ActivarMembresiasSecundarias — acciones
    // =================================================================
    async onToggleActivarSecundarias(nuevoValor) {
      if (this.activarSecundarias.loading.toggle) return;

      // Confirmación destructiva al desactivar — al excluir la empresa, las
      // membresías secundarias en estado PAGADO quedan congeladas: no se
      // promueven aunque la principal del socio venza.
      if (!nuevoValor) {
        const result = await this.$swal.fire({
          icon: "warning",
          title: "¿Desactivar activación automática de secundarias?",
          html:
            `Tus membresías secundarias <strong>DEJARÁN</strong> de activarse ` +
            `automáticamente cuando la membresía principal del socio venza.<br/><br/>` +
            `<small class="text-muted">` +
            `Las secundarias en estado <strong>PAGADO</strong> quedarán congeladas ` +
            `hasta que vuelvas a activar este proceso o las actives manualmente. ` +
            `Los socios no verán activarse sus planes adicionales.` +
            `</small>`,
          showCancelButton: true,
          confirmButtonText: "Sí, desactivar",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#ef8157",
          cancelButtonColor: "#6bd098",
          focusCancel: true,
        });
        if (!result.isConfirmed) return;
      }

      this.activarSecundarias.loading.toggle = true;
      try {
        const dataOut = await this.callMiEmpresa(OP.AS_TOGGLE, {
          jobName: JOB_AS,
          activa: !!nuevoValor,
        });
        this.activarSecundarias.guardado.participaEnProceso = !!nuevoValor;
        this.aplicarModificadoPor(dataOut);
        this.toastExito(
          nuevoValor
            ? "Activación automática de secundarias: ACTIVADA."
            : "Activación automática de secundarias: DESACTIVADA."
        );
      } catch (err) {
        this.mostrarErrorGuardado(err);
      } finally {
        this.activarSecundarias.loading.toggle = false;
      }
    },

    // =================================================================
    //  Llamada genérica a /dashboard
    // =================================================================
    async callMiEmpresa(operacion, body) {
      try {
        const resp = await this.$CentralGymOPS_API({
          method: "POST",
          url: "/",
          data: {
            Header: { Operacion: operacion },
            Auth: { Token: sessionStorage.getItem(TOKEN_KEY) },
            Parameters: {},
            Body: body || {},
          },
        });
        const result = resp.data && resp.data.result;
        if (result && result.status === "error") {
          const err = new Error(result.message || "Error en la operación");
          err.httpCode = result.httpCode;
          err.serverResult = result;
          throw err;
        }
        return resp.data && resp.data.dataOut;
      } catch (err) {
        // Normaliza el error de axios (red / HTTP no-2xx) al mismo shape
        // que el "error en respuesta 200 con result.status='error'", para que
        // los handlers río arriba puedan inspeccionar `err.httpCode` siempre.
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.result &&
          err.httpCode === undefined
        ) {
          err.httpCode = err.response.data.result.httpCode;
          err.serverResult = err.response.data.result;
        }
        throw err;
      }
    },

    // =================================================================
    //  Utilidades UI
    // =================================================================
    aplicarModificadoPor(dataOut) {
      if (dataOut && dataOut.modificadoPor) {
        this.modificadoPor = dataOut.modificadoPor;
      }
    },
    esConflictoExclusion(err) {
      return !!(err && err.httpCode === 409);
    },
    toastExito(titulo) {
      this.$swal.fire({
        position: "top-end",
        icon: "success",
        title: titulo,
        showConfirmButton: false,
        timer: 3500,
        toast: true,
      });
    },
    extraerMensajeError(err) {
      if (!err) return null;
      if (err.serverResult && err.serverResult.message) {
        return err.serverResult.message;
      }
      if (
        err.response &&
        err.response.data &&
        err.response.data.result &&
        err.response.data.result.message
      ) {
        return err.response.data.result.message;
      }
      return err.message || null;
    },
    mostrarErrorGuardado(err) {
      this.$logger.error("[ProcesosAutomaticos] Error en operación:", err);
      this.$swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          this.extraerMensajeError(err) ||
          "No se pudo completar la operación. Reintenta o contacta a soporte.",
        confirmButtonColor: "#6bd098",
      });
    },
  },
};
</script>

<style scoped>
.procesos-automaticos {
  padding-bottom: 2rem;
}
.page-title {
  font-weight: 600;
}
.seccion-header {
  margin: 0.5rem 0 0.75rem;
  padding: 0.25rem 0.5rem;
}
.seccion-header h5 {
  font-weight: 600;
}
.config-card {
  margin-bottom: 0.75rem;
  border: 1px solid #ececec;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.config-card-header {
  background-color: #fafafa;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
}
.header-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  flex-shrink: 0;
}
.header-icon--blue {
  background: linear-gradient(135deg, #4b9cf5, #2266c5);
}
.header-icon--green {
  background: linear-gradient(135deg, #6bd098, #3da76b);
}
.header-icon--purple {
  background: linear-gradient(135deg, #a583e0, #6d48b8);
}
.header-icon--orange {
  background: linear-gradient(135deg, #ffb878, #ef8157);
}
.toggle-wrapper {
  flex-shrink: 0;
}
.estado-label {
  font-weight: 600;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.dias-section {
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}
.acciones-dias .btn {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}
.acciones-dias .btn:last-child {
  margin-right: 0;
}
.latencia-note {
  margin: 0.5rem 0 1.25rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
  background-color: #f6f6f6;
  border-left: 3px solid #b0b0b0;
  border-radius: 4px;
}
code {
  background-color: rgba(0, 0, 0, 0.04);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.85em;
}
</style>
